import { useCallback, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import KeyRound from "lucide-react/dist/esm/icons/key-round";
import KeySquare from "lucide-react/dist/esm/icons/key-square";
import LogIn from "lucide-react/dist/esm/icons/log-in";
import Lock from "lucide-react/dist/esm/icons/lock";
import Mail from "lucide-react/dist/esm/icons/mail";
import Cookie from "lucide-react/dist/esm/icons/cookie";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { pluralize } from "@/lib/pluralize";
import { BackButton } from "@/components/ui/primitives/back-button";
import { useSession } from "@/hooks/use-session";
import { useApiTokens } from "@/hooks/use-api-tokens";
import { usePasskeys } from "@/hooks/use-passkeys";
import { useHasPassword } from "@/hooks/use-has-password";
import { useHasOidcAccount } from "@/hooks/use-has-oidc-account";
import { authClient } from "@/lib/auth-client";
import { resolveAccountIdentity } from "@/lib/account-identity";
import { useLinkedProviderIds } from "@/hooks/use-linked-provider-ids";
import { DeleteAccountModal } from "@/features/auth/components/delete-account-modal";
import { readsAsReauthenticationReturn } from "@/lib/account-reauthentication";
import { toStringSearchParams } from "@/lib/mcp-auth-flow";
import {
  NavigationMenu,
  NavigationMenuButtonItem,
  NavigationMenuItem,
  NavigationMenuLinkItem,
  NavigationMenuToggleItem,
  NavigationMenuItemIcon,
  NavigationMenuItemLabel,
  NavigationMenuItemTrailing,
} from "@/components/ui/composites/navigation-menu/navigation-menu-items";
import { setAnalyticsConsent, track, ANALYTICS_EVENTS } from "@/lib/analytics";
import { useEffectiveConsent } from "@/hooks/use-effective-consent";
import { Text } from "@/components/ui/primitives/text";
import { SettingsPlanMenuItem } from "@/features/dashboard/components/plan-menu";
import {
  fetchAuthCapabilitiesWithApi,
  resolveOidcProviderName,
} from "@/lib/auth-capabilities";

export const Route = createFileRoute("/(dashboard)/dashboard/settings/")({
  loader: async ({ context }) => {
    const authCapabilities = await fetchAuthCapabilitiesWithApi(context.fetchApi);
    return { authCapabilities, commercialMode: context.runtimeConfig.commercialMode };
  },
  component: SettingsPage,
  validateSearch: toStringSearchParams,
});

function SettingsPage() {
  const { authCapabilities, commercialMode } = Route.useLoaderData();
  const { user } = useSession();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { data: hasPassword = false } = useHasPassword();
  const { data: linkedProviderIds = [] } = useLinkedProviderIds();
  const { label: accountLabel, value: accountValue } = resolveAccountIdentity(user);
  const { data: apiTokens = [] } = useApiTokens();
  const { data: passkeys = [] } = usePasskeys(authCapabilities.supportsPasskeys);
  const oidcEnabled = authCapabilities.socialProviders.oidc === true;
  const { data: hasOidcAccount = false } = useHasOidcAccount(oidcEnabled);
  const [oidcLinkError, setOidcLinkError] = useState<string | null>(null);
  const [isLinkingOidc, setIsLinkingOidc] = useState(false);
  const analyticsConsent = useEffectiveConsent();
  const handleAnalyticsToggle = useCallback((checked: boolean) => {
    track(ANALYTICS_EVENTS.analytics_consent_changed, { granted: checked });
    setAnalyticsConsent(checked);
  }, []);
  const [deleteOpen, setDeleteOpen] = useState(() => readsAsReauthenticationReturn(search));

  const handleDeleteOpenChange = (open: boolean) => {
    setDeleteOpen(open);
    if (!open && readsAsReauthenticationReturn(search)) {
      navigate({ to: "/dashboard/settings", replace: true });
    }
  };

  const handleLinkOidc = async () => {
    setOidcLinkError(null);
    setIsLinkingOidc(true);
    const { error } = await authClient.linkSocial({
      callbackURL: "/dashboard/settings",
      provider: "oidc",
    });
    if (error) {
      setOidcLinkError(error.message ?? "Failed to start linking.");
      setIsLinkingOidc(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <BackButton />
      <NavigationMenu>
        <NavigationMenuItem>
          <NavigationMenuItemIcon>
            <Mail size={15} />
          </NavigationMenuItemIcon>
          <NavigationMenuItemLabel>{accountLabel}</NavigationMenuItemLabel>
          <NavigationMenuItemTrailing>
            <Text size="sm" tone="muted" className="truncate">{accountValue}</Text>
          </NavigationMenuItemTrailing>
        </NavigationMenuItem>
      </NavigationMenu>
      <NavigationMenu>
        {hasPassword && (
          <NavigationMenuLinkItem to="/dashboard/settings/change-password">
            <NavigationMenuItemIcon>
              <Lock size={15} />
            </NavigationMenuItemIcon>
            <NavigationMenuItemLabel>Change Password</NavigationMenuItemLabel>
            <NavigationMenuItemTrailing />
          </NavigationMenuLinkItem>
        )}
        {authCapabilities.supportsPasskeys && (
          <NavigationMenuLinkItem to="/dashboard/settings/passkeys">
            <NavigationMenuItemIcon>
              <KeyRound size={15} />
            </NavigationMenuItemIcon>
            <NavigationMenuItemLabel>Passkeys</NavigationMenuItemLabel>
            <NavigationMenuItemTrailing>
              <Text size="sm" tone="muted">
                {pluralize(passkeys.length, "passkey", "passkeys")}
              </Text>
            </NavigationMenuItemTrailing>
          </NavigationMenuLinkItem>
        )}
        {oidcEnabled && (
          <NavigationMenuButtonItem
            onClick={hasOidcAccount ? undefined : handleLinkOidc}
            disabled={hasOidcAccount || isLinkingOidc}
          >
            <NavigationMenuItemIcon>
              <LogIn size={15} />
            </NavigationMenuItemIcon>
            <NavigationMenuItemLabel>
              {`Link ${resolveOidcProviderName(authCapabilities)}`}
            </NavigationMenuItemLabel>
            <NavigationMenuItemTrailing>
              <Text size="sm" tone="muted">
                {hasOidcAccount ? "Linked" : "Not linked"}
              </Text>
            </NavigationMenuItemTrailing>
          </NavigationMenuButtonItem>
        )}
        {commercialMode && <SettingsPlanMenuItem />}
        <NavigationMenuLinkItem to="/dashboard/settings/api-tokens">
          <NavigationMenuItemIcon>
            <KeySquare size={15} />
          </NavigationMenuItemIcon>
          <NavigationMenuItemLabel>API Tokens</NavigationMenuItemLabel>
          <NavigationMenuItemTrailing>
            <Text size="sm" tone="muted">
              {pluralize(apiTokens.length, "token", "tokens")}
            </Text>
          </NavigationMenuItemTrailing>
        </NavigationMenuLinkItem>
      </NavigationMenu>
      {oidcLinkError && <Text size="sm" tone="danger">{oidcLinkError}</Text>}
      <NavigationMenu>
        <NavigationMenuToggleItem checked={analyticsConsent} onCheckedChange={handleAnalyticsToggle}>
          <NavigationMenuItemIcon>
            <Cookie size={15} />
          </NavigationMenuItemIcon>
          <NavigationMenuItemLabel>Analytics Cookies</NavigationMenuItemLabel>
        </NavigationMenuToggleItem>
      </NavigationMenu>
      <NavigationMenu>
        <NavigationMenuButtonItem onClick={() => setDeleteOpen(true)}>
          <NavigationMenuItemIcon>
            <Trash2 size={15} className="text-destructive" />
          </NavigationMenuItemIcon>
          <Text size="sm" tone="danger">Delete Account</Text>
        </NavigationMenuButtonItem>
      </NavigationMenu>
      <DeleteAccountModal
        open={deleteOpen}
        onOpenChange={handleDeleteOpenChange}
        user={user}
        capabilities={authCapabilities}
        hasPassword={hasPassword}
        linkedProviderIds={linkedProviderIds}
        passkeyCount={passkeys.length}
      />
    </div>
  );
}
